import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import {
  useGetLeadTemplate,
  useGetSourceChannelList,
  useSearchLeadHistoryList,
} from "../../../api/leadsManagement";
import { UserActions } from "../../../models/Authorisation";
import { usePermissionStore } from "../../../stores/permissionStore";
import { useUserStore } from "../../../stores/userStore";
import { LeadImportHistorySearchState } from "../../../types/leads.types";

const DEBOUNCE_MS = 300;

const useImportLeads = () => {
  const permissionToken = usePermissionStore.getState().permissionToken ?? "";
  const { leadsUserAction } = useUserStore();

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const canEdit =
    !!leadsUserAction &&
    Object.values(leadsUserAction).some(actions =>
      actions?.includes(UserActions.Edit)
    );

  const formMethods = useForm<LeadImportHistorySearchState>({
    defaultValues: {
      channel: "All Channels",
    },
  });

  const { control, getValues } = formMethods;

  const channel = useWatch({
    control,
    name: "channel",
    defaultValue: "All Channels",
  });

  const searchLeadImportHistoryMutation = useSearchLeadHistoryList();

  const { data: leadImportHistoryData, isLoading: isLeadImportHistoryLoading } =
    searchLeadImportHistoryMutation;

  const debounceTimerRef = useRef<number | null>(null);
  const lastSignatureRef = useRef<string>("");

  const buildPayload = useCallback(
    (page: number) => {
      const effectiveChannel =
        channel && channel !== "All Channels" ? [channel] : [];
      return {
        permission_Token: permissionToken,
        filterSourceChannels: effectiveChannel,
        pagination: {
          CurrentPage: page,
          pageSize,
        },
      };
    },
    [permissionToken, channel, pageSize]
  );

  const fireRequest = useCallback(
    (page: number) => {
      if (!permissionToken) return;
      const payload = buildPayload(page);
      const signature = JSON.stringify(payload);
      if (signature === lastSignatureRef.current) return;
      lastSignatureRef.current = signature;
      searchLeadImportHistoryMutation.mutate(payload);
    },
    [permissionToken, buildPayload, searchLeadImportHistoryMutation]
  );

  const debouncedFireRequest = useCallback(
    (page: number) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      debounceTimerRef.current = window.setTimeout(() => {
        fireRequest(page);
      }, DEBOUNCE_MS);
    },
    [fireRequest]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const lastChannelRef = useRef<string | undefined>(channel);
  useEffect(() => {
    if (lastChannelRef.current !== channel) {
      lastChannelRef.current = channel;
      setCurrentPage(prev => (prev === 1 ? prev : 1));
    }
  }, [channel]);

  useEffect(() => {
    if (!permissionToken) return;
    debouncedFireRequest(currentPage);
  }, [permissionToken, currentPage, channel, debouncedFireRequest]);

  const onPageChange = (page: number) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const refreshHistory = (page?: number, options?: { immediate?: boolean }) => {
    if (typeof page === "number" && page > 0 && page !== currentPage) {
      setCurrentPage(page);
      return;
    }
    const shouldImmediate = options?.immediate ?? false;
    if (shouldImmediate) {
      fireRequest(currentPage);
    } else {
      debouncedFireRequest(currentPage);
    }
  };

  const getLeadTemplateQuery = useGetLeadTemplate(
    {
      permission_Token: permissionToken,
      channelName:
        getValues("channel") && getValues("channel") !== "All Channels"
          ? getValues("channel")
          : "",
    },
    { enabled: false }
  );

  const leadImportData = leadImportHistoryData?.data?.imports || [];
  const totalImportedLead = leadImportHistoryData?.data?.total || 0;
  const leadImportPagination = leadImportHistoryData?.data?.pagination;

  const { data: sourceChannelListResponse } = useGetSourceChannelList({
    permission_Token: permissionToken,
  });

  const sourceChannelOptionsList = useMemo(() => {
    const defaultOption = { label: "All Channels", value: "All Channels" };
    const mappedOptions = (sourceChannelListResponse?.data?.options || []).map(
      (option: string) => ({
        label: option,
        value: option,
      })
    );
    return [defaultOption, ...mappedOptions];
  }, [sourceChannelListResponse]);

  return {
    canEdit,
    leadImportData,
    totalImportedLead,
    leadImportPagination,
    isLeadImportHistoryLoading,
    currentPage,
    onPageChange,
    pageSize,
    sourceChannelOptionsList,
    formMethods,
    getLeadTemplateQuery,
    refreshHistory,
    onRowClick: () => {
      // TODO: implement row click logic
    },
  };
};

export default useImportLeads;
